/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { useMutation } from '@apollo/client/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { PaymentRequestFormInput } from '@web/src/features/admin/payment-request-form/payment-request-form'
import PaymentRequestForm from '@web/src/features/admin/payment-request-form/payment-request-form'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { ADMIN_CREATE_PAYMENT_REQUEST } from '@web/src/lib/mutations'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { useState } from 'react'
import toast from 'react-hot-toast'

export const Route = createFileRoute('/(auth)/(admin-layout)/admin/payments/create')({
  component: CreatePaymentRequestPage,
  head: () =>
    seo({
      title: `Create Payment Request | ${env.VITE_APP_NAME}`,
      description: 'Create a new payment request',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
})

function CreatePaymentRequestPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [showSpinner, setShowSpinner] = useState(false)

  const [adminCreatePaymentRequest] = useMutation(ADMIN_CREATE_PAYMENT_REQUEST, {
    onCompleted: () => {
      toast.success(t('Payment request created'))
      setShowSpinner(false)
      navigate({ to: '/admin/payments', search: { tab: 'pending' } } as never)
    },
    onError: (err) => {
      toast.error(err.message)
      setShowSpinner(false)
    },
  })

  const onSave = (input: PaymentRequestFormInput) => {
    if (!input.profile?.id) {
      toast.error(t('Please select a profile'))
      return
    }
    setShowSpinner(true)
    adminCreatePaymentRequest({
      variables: {
        profileId: input.profile.id,
        input: {
          days: input.days,
          partnerIds: input.partnerIds,
          total: input.total,
        },
      },
    })
  }

  return (
    <div className="mx-auto w-full max-w-2xl pt-4">
      <div className="flex w-full flex-col gap-4">
        <h1 className="w-full text-center font-semibold text-3xl tracking-tight">{t('Create Payment Request')}</h1>
        <PaymentRequestForm mode="create" onSave={onSave} showSpinner={showSpinner} />
      </div>
    </div>
  )
}
